import {useCallback, useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  ProfilerNotebookFeatures,
  TABLE_CELL_EDIT_WITH_RENDER_FINISHED_EVENT,
  TABLE_CELL_EDIT_WITH_RENDER_STARTED_EVENT,
} from 'performance/processNotebookEvents';
import {TablePerformanceTestUtils} from 'test/lib/cypress/shared/TablePerformanceTestUtils';

import {TableCellEditorProps} from '../TableCellEditor';

type CellEnterHandler = TableCellEditorProps['cellEnterHandler'];

function isExpectedText(text: string): boolean {
  return text === TablePerformanceTestUtils.expectedCellValue;
}

function startTimingEvent(): Function {
  const id = uuidv4();
  const measure = `${ProfilerNotebookFeatures.TABLE_CELL_EDIT_WITH_RENDER}-${id}`;
  const measureOptions = {start: `${id}-start`, end: `${id}-end`};
  const startEvent = {...TABLE_CELL_EDIT_WITH_RENDER_STARTED_EVENT, id};
  const finalEvent = {...TABLE_CELL_EDIT_WITH_RENDER_FINISHED_EVENT, id};

  performance.mark(measureOptions.start);
  logProfilerTimingEvent(startEvent);

  return function finishTimingEvent() {
    logProfilerTimingEvent(finalEvent);
    performance.mark(measureOptions.end);
    performance.measure(measure, measureOptions);
  };
}

/**
 * Adds instrumentation to the cellEnterHandler to track the time it takes to change a cell's text.
 *
 * This works under the assumption that the table cell invokes a callback which sets state on its parent, and
 * that the parent then uses that state to update the cell's text. We measure how much time it takes from the
 * time the callback is invoked to the time the hook receives the updated value from the parent.
 *
 * Currently only applies to a very specific expected value that is used in a synethetic (performance) test.
 */
export function useTimedCellEnterHandler({
  enabled,
  text,
  cellEnterHandler,
}: {
  enabled: boolean;
  text: string;
  cellEnterHandler: CellEnterHandler;
}): CellEnterHandler {
  const [timingEvent, setTimingEvent] = useState<{finish: Function} | null>(null);
  const isFinishing = useRef(false);

  // Finish the timing event when the cell is done rendering and the expected text is present
  useEffect(() => {
    if (!enabled || isFinishing.current || !timingEvent || !isExpectedText(text)) {
      return;
    }

    isFinishing.current = true;

    // It is possible that the component invoking this hook is re-rendered multiple times consecutively. We want
    // to ensure the instrumentation timing encompasses all of these intermediate re-renders as they add to the
    // overall time it takes for the change to complete. We do this by queueing a JS microtask after the current
    // React lifecycle loop via Promise.resolve. We use a ref to ensure the callback only runs once.
    Promise.resolve().then(() => {
      timingEvent.finish();
      setTimingEvent(null);
      isFinishing.current = false;
    });
  }, [enabled, text, timingEvent]);

  // Start the timing event when the expected text is entered
  return useCallback(
    (event) => {
      if (enabled && isExpectedText(event.text)) {
        setTimingEvent({finish: startTimingEvent()});
      }
      cellEnterHandler(event);
    },
    [enabled, cellEnterHandler]
  );
}
